import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ParkingRecovery } from 'services/ParkingAgentService';
import { useAgentRun } from 'components/parking-agent/useAgentRun';
import RecoveryPreview from 'components/parking-agent/RecoveryPreview';
import { RecoverySelection } from 'components/parking-agent/recoveryCsv';

interface ParkingRecoveryPanelProps {
  siteId: number;
  filename: string;
  sourceNames: string[];
  /** 레이어 선택 탭에서 지금 고른 레이어 — 원본 레이어 목록 맨 위에 둔다. */
  selectedNames: string[];
  onSelectionChange: (selection: RecoverySelection | null) => void;
}

const SELECTED_GROUP = '선택한 레이어';
const OTHER_GROUP = '도면의 다른 레이어';

/** 원본 레이어 후보 — 선택한 레이어를 먼저, 나머지 도면 레이어를 뒤에(groupBy 는 정렬된 목록이 필요하다). */
export function recoveryLayerOptions(
  selectedNames: string[],
  sourceNames: string[],
): string[] {
  const selected = new Set(selectedNames);
  return [
    ...sourceNames.filter((name) => selected.has(name)),
    ...sourceNames.filter((name) => !selected.has(name)),
  ];
}

const ParkingRecoveryPanel: React.FC<ParkingRecoveryPanelProps> = ({
  siteId,
  filename,
  sourceNames,
  selectedNames,
  onSelectionChange,
}) => {
  const [layer, setLayer] = useState('PLAN');
  const [block, setBlock] = useState(
    sourceNames.find((name) => name === 'xref_주차장') || '',
  );
  const { run, loading, busy, error, start } = useAgentRun<ParkingRecovery>(
    siteId,
    'parking_recovery',
    filename,
  );
  return (
    <Stack spacing={2}>
      <Typography sx={{ overflowWrap: 'anywhere' }}>{filename}</Typography>
      <Typography variant="body2" color="text.secondary">
        레이어가 합쳐진 도면에서 선분 경계를 분석합니다. 원본 레이어와 블록을
        지정하면 복원할 범위를 좁힐 수 있습니다.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Autocomplete
          freeSolo
          fullWidth
          disabled={busy}
          options={recoveryLayerOptions(selectedNames, sourceNames)}
          groupBy={(name) =>
            selectedNames.includes(name) ? SELECTED_GROUP : OTHER_GROUP
          }
          // value 도 같이 줘야 열 때 입력값(기본 PLAN)으로 거르지 않고 전체 목록을 보여준다.
          value={layer}
          onChange={(_, value) => setLayer(value ?? '')}
          inputValue={layer}
          onInputChange={(_, value) => setLayer(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="원본 레이어"
              inputProps={{ ...params.inputProps, maxLength: 255 }}
              helperText="선택한 레이어에서 고르거나 직접 입력 · 예: PLAN"
            />
          )}
        />
        <TextField
          label="원본 블록 (선택)"
          value={block}
          onChange={(e) => setBlock(e.target.value)}
          fullWidth
          disabled={busy}
          inputProps={{ maxLength: 255 }}
          helperText="예: xref_주차장 · 비우면 배치된 블록 전체"
        />
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      <Box>
        <Button
          variant="contained"
          disabled={busy || loading || !layer.trim()}
          onClick={() => {
            onSelectionChange(null);
            void start({
              kind: 'parking_recovery',
              filename,
              layer: layer.trim(),
              block: block.trim(),
            });
          }}
          sx={{ minHeight: 44 }}
        >
          {busy ? '주차면 복원 중…' : '복원 후보 만들기'}
        </Button>
      </Box>
      {(busy || loading) && (
        <Box role="status">
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1 }}>
            {busy
              ? '대형 도면은 수 분 걸릴 수 있습니다. 다른 레이어를 선택하는 동안에도 복원은 계속됩니다.'
              : '저장된 작업을 확인하고 있습니다.'}
          </Typography>
        </Box>
      )}
      {run?.status === 'failed' && <Alert severity="error">{run.error}</Alert>}
      {!busy &&
        !loading &&
        !error &&
        run?.status === 'completed' &&
        run.result && (
          <RecoveryResult
            key={run.id}
            result={run.result}
            onSelectionChange={onSelectionChange}
          />
        )}
    </Stack>
  );
};

const RecoveryResult: React.FC<{
  result: ParkingRecovery;
  onSelectionChange: (selection: RecoverySelection | null) => void;
}> = ({ result, onSelectionChange }) => {
  const [selected, setSelected] = useState(
    () => new Set(result.candidates.map((c) => c.id)),
  );
  useEffect(() => {
    const candidates = result.candidates.filter((candidate) =>
      selected.has(candidate.id),
    );
    onSelectionChange(
      candidates.length ? { candidates, sourceHash: result.sha256 } : null,
    );
  }, [result, selected, onSelectionChange]);
  const toggle = (id: string) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <Stack spacing={2}>
      <Typography fontWeight={700}>
        검수 전 주차면 후보{' '}
        {result.candidates.filter((c) => c.parking_type !== 's-pillar').length}개
        · 기둥 후보{' '}
        {result.candidates.filter((c) => c.parking_type === 's-pillar').length}개
      </Typography>
      <Typography variant="body2" color="text.secondary">
        분석 범위: {result.layer} · {result.block || '배치된 블록 전체'} · 원본{' '}
        {result.sha256.slice(0, 12)}
      </Typography>
      {result.warnings.length > 0 && (
        <Alert severity="info">{result.warnings.join(' ')}</Alert>
      )}
      {result.candidates.length > 0 ? (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              onClick={() =>
                setSelected(new Set(result.candidates.map((c) => c.id)))
              }
              sx={{ minHeight: 44 }}
            >
              후보 전체 선택
            </Button>
            <Button
              onClick={() => setSelected(new Set())}
              sx={{ minHeight: 44 }}
            >
              후보 전체 해제
            </Button>
          </Stack>
          <RecoveryPreview
            result={result}
            selected={selected}
            onToggle={toggle}
          />
          <Typography variant="body2">
            선택한 후보를 일반 주차면으로 가져옵니다. 장애인·전기차 등 용도는
            맵에서 확인하고 수정해주세요.
          </Typography>
          <Alert severity="success">
            선택한 복원 후보 {selected.size}개가 가져올 목록에 포함됩니다. 다른
            레이어도 선택한 뒤 창 아래의 가져오기를 눌러주세요.
          </Alert>
        </>
      ) : (
        <Alert severity="warning">
          복원할 수 있는 닫힌 주차면 경계를 찾지 못했습니다. 원본 레이어·블록과
          도면 단위를 확인해주세요.
        </Alert>
      )}
    </Stack>
  );
};

export default ParkingRecoveryPanel;
